<template lang="pug">
	.easy-creative--script-builder
		.flex-col.space-large
			.flex-col.space-medium
				.flex-col.space-large
					.flex-col.space-medium
						.flex-row.justify.space
							.flex-row.space-large(:class="{ 'disabled-controls': isLoading }")
								.flex-row.align-center.space
									.font-big.upper.grey {{$tCaption('Script')}}
									.flex-row.space
										DxButton(v-if="numberOfVariantsSpecified", icon="refresh", @click="reloadScript", type="normal", :hint="$tCaption('Re-generate script')")
								.flex-row.space.align-center(v-if="numberOfVariantsSpecified && responses.length > 1")
									.font.upper.grey {{$tCaption('Variant:')}}
									.flex-row
										.adt-tab(v-for="(r, rIdx) in responses", :class="{ active: currentResponseIdx === rIdx }", @click="selectResponse(r, rIdx)") {{rIdx + 1}}
							.flex-row.align-center.space
								DxButton(v-if="selectedText", :text="$tCaption('Fix pronunciation')", icon="fas fa-pen-nib", @click="showPronunciationDialog")
						.flex-col.space-medium(v-if="!numberOfVariantsSpecified", :class="{ 'disabled-controls': isLoading }")
							.flex-row.space.align-center
								span {{$tCaption('Number of script variations to generate')}}: 
								b {{numberOfVariants}}
							DxSlider(:value.sync="numberOfVariants", :min="1", :max="5")
						.flex-col.space-medium(v-else)
							.line-loader(v-if="isLoading", :style="{ '--percentage': `${loadingPercent}%` }", :text="status || $tCaption('Generating script')")
							StreamVisualizer(v-show="isLoading", :runId="generationRunId", :info="info", :prepareScriptVAMFormat="prepareScriptVAMFormat")
							.flex-col.space(v-if="!isLoading && script")
								template(v-if="isText")
									.flex-row.space-medium.align-center
										.font-big(v-if="!isEditingScript") {{script}} 
										DxTextArea.flex-1.font-big.no-borders(v-else, :value.sync="script", styling-mode="text", :height="200")
										.flex-row.space(v-if="!isEditingScript")
											DxButton(v-if="isText", icon="edit", @click="isEditingScript = true", type="normal", :hint="$tCaption('Edit script')")
									DxButton(v-if="isEditingScript", icon="check", :text="$tCaption('Confirm', '@actions')" @click="changeScript", type="normal")
								template(v-if="isArray")
									ScriptTable(:script="script", :info="info", :isScriptItemsHighlighted="isScriptItemsHighlighted", :toggleVoiceInfo="toggleVoiceInfo")
										template(v-slot:actions="{ item }")
											.action-button.flex-row.center.align-center(:title="$tCaption('Delete')", @click="deleteItem(item)")
												fas(icon="times")
								.flex-row.align-center.space.justify
									.flex-row.space.align-center
									.flex-row.space.align-center
										.font-tiny.grey(v-if="wordsCount", v-html="$tCaption('<b>WORDSCOUNT</b> words').replace(/WORDSCOUNT/g, wordsCount)")
				.spacer
				.flex-col.space-medium.full-width
					.actions-divider.full-width
					.flex-row.max-limited.navigation-row.justify-end
						.flex-row.space
							DxButton(:text="$tCaption('Back', '@actions')", @click="onBackClick", :activeStateEnabled="false", :focusStateEnabled="false")
							DxButton(:text="numberOfVariantsSpecified ? $tCaption('Next', '@actions') : $tCaption('Generate script')", type="default", @click="onNextClick", :activeStateEnabled="false", :focusStateEnabled="false", :disabled="!!isLoading")
							DxButton(v-if="responses.length && !numberOfVariantsSpecified", :text="$tCaption('Next', '@actions')", type="default", @click="numberOfVariantsSpecified = true", :activeStateEnabled="false", :focusStateEnabled="false", :disabled="!!isLoading")
		SSMLTextPronunciationTool(ref="pronunciationTool", :voice="selectedScriptItem && selectedScriptItem.actualVoice", @selection-change="onPronunciationToolSelectionChange", 
			@change="onPronunciationToolChange", :locale="info.language().locale", @save-phoneme="onPronunciationToolSavePhoneme")
		DxPopover(v-if="tooltipVoice", :visible="true", :target="tooltipTarget" :hide-on-outside-click="true", @hidden="tooltipVoice = null", :position="tooltipPositionSettings", :deferRendering="true", :onContentReady="onPopupContentReady")
			.flex-row.align-start.text-start.easy-creative--script-builder--voice-details-tooltip(:class="{ loaded: voiceLoaded }")
				VoiceDetails(:voice="tooltipVoice", :hideNLCSSounds="true", :compact="true", @voice-details="voiceLoaded = true")
		EasyCreativeChatBot(v-if="chatBotVisible && firstGenerated && !info.skippedSteps.script", ref="chatBot", :info="info")
</template>

<script>
	import Loader from 'app-core/Loader.vue';
	import DxButton from 'devextreme-vue/button';
	import DxTextArea from 'devextreme-vue/text-area';
	import DxTextBox from 'devextreme-vue/text-box';
	import EditableLabel from 'app-core/EditableLabel.vue';
	import { getVoicesAdjectives, activateVoice } from 'view/insertion/cognitive-arrays';
	import SSMLTextPronunciationTool from 'view/insertion/SSMLTextPronunciationTool.vue';
	import _ from 'lodash';
	import TagEditor from './EasyCreativeTagEditor.vue';
	import Switcher from 'app-core/Switcher.vue';
	import DxPopover from 'devextreme-vue/tooltip';
	import VoiceDetails from 'view/insertion/VoiceDetails.vue';
	import EasyCreativeChatBot from './EasyCreativeChatBot.vue';
	import DxSlider from 'devextreme-vue/slider';
	import StreamVisualizer from './EasyCreativeScriptStreamVisualizer.vue';
	import ScriptTable from './EasyCreativeScriptTable.vue';

	export default {
		translationCategory: 'creative.EasyCreative',
		name: 'easy-creative-script-builder',
		props: ['info'],
		components: {
			DxButton, Loader, DxTextArea, EditableLabel, SSMLTextPronunciationTool, TagEditor, Switcher, DxPopover, VoiceDetails, DxTextBox,
			EasyCreativeChatBot, DxSlider, StreamVisualizer, ScriptTable
		},
		data() {
			return {
				isLoading: false,
				script: null,
				scriptText: null,
				isEditingScript: false,
				selectedText: null,
				selectedScriptItem: null,
				scriptProcessed: false,
				testPayload: false && this.$auth.isLocalhost() && __DEBUG__,
				status: null,
				loadingPercent: 0,
				tooltipVoice: null,
				tooltipTarget: null,
				tooltipPositionSettings: { my: 'left center', at: 'right center', collision: 'flipfit' },
				debug: {
					generateResult: null,
					customInfo: {}
				},
				voicesActivatedTime: null,
				voicesMaxActivationDuration: null,
				lastResponse: null,
				numberOfVariantsSpecified: false,
				processingAttachment: null,
				images: [],
				generatingImages: false,
				numberOfVariants: 1,
				responses: [],
				currentResponseIdx: 0,
				firstGenerated: false,
				chatBotVisible: false,
				assistantThreadId: null,
				assistantId: null,
				assistantInitiated: false,
				isScriptItemsHighlighted: 0,
				assistantResponseIdx: 0,
				voiceLoaded: false,
				requestVoiceDebugInfo: [],
				generationRunId: null
			};
		},
		computed: {
			isArray() {
				return Array.isArray(this.script);
			},
			isText() {
				return !this.isArray;
			},
			music() {
				return this.isArray && this.script.find(f => `${f.type}`.toLowerCase() === 'music') || null;
			},
			duration() {
				return this.isArray && this.script.find(f => `${f.type}`.toLowerCase() === 'duration') || null;
			},
			visibleScriptItems() {
				return this.isArray && this.script?.filter(f => !f.hide) || [];
			},
			isFixedDuration() {
				return this.duration?.isFixed;
			},
			text() {
				return this.script && this.isArray ? this.script.filter(f => f.type === 'text').map(f => f.value).join('. ') : this.script || '';
			},
			wordsCount() {
				return this.text.split(/[ ]/g).filter(f => f).length;
			},
			charsCount() {
				return this.text.split(/[ ]/g).filter(f => f).join('').length;
			},
			leftForVoicesActivation: {
				get: function() {
					return this.voicesActivatedTime ? Math.max(0, this.voicesMaxActivationDuration - (new Date() - this.voicesActivatedTime) / 1000) : 0;
				},
				cache: false
			}
		},
		mounted() {
			if (__DEBUG__)
				window.easyCreativeScriptBuilder = this;
			document.addEventListener('selectionchange', this.calcSelectionText);
			document.addEventListener('keydown', this.onKeyDown);
			this.$eventsBus.$on('signalr-on-notification', this.signalrOnNotification);
			this.$eventsBus.$on('chat-bot-user-message', this.onChatBotUserMessage);
			this.$eventsBus.$on('chat-bot-user-attachment', this.onChatBotUserAttachment);
			this.$eventsBus.$on('easy-creative--request-voices--debug-info', this.onRequestVoicesDebugInfo);
			this.$eventsBus.$on('easy-creative--add-debug-info', this.onAddDebugInfo);

			this.info.addDebugFunc(this.debugFunc);
		},
		destroyed() {
			delete this.$lastRenderedItem;
			delete this.$lastColor;
			delete this.$lastSelection;
			delete this.$testScriptIdx;

			document.removeEventListener('selectionchange', this.calcSelectionText);
			document.removeEventListener('keydown', this.onKeyDown);

			this.$eventsBus.$off('signalr-on-notification', this.signalrOnNotification);
			this.$eventsBus.$off('chat-bot-user-message', this.onChatBotUserMessage);
			this.$eventsBus.$off('chat-bot-user-attachment', this.onChatBotUserAttachment);
			this.$eventsBus.$off('easy-creative--request-voices--debug-info', this.onRequestVoicesDebugInfo);
			this.$eventsBus.$off('easy-creative--add-debug-info', this.onAddDebugInfo);

			this.info.removeDebugFunc(this.debugFunc);
			
			if (this.$percentLoadingTimeout) {
				clearTimeout(this.$percentLoadingTimeout);
				delete this.$percentLoadingTimeout;
			}

			this.removeChatBot();
		},
		methods: {
			onAddDebugInfo(prop, value) {
				this.debug.customInfo[prop] = value;
			},
			onKeyDown(e) {
				if (this.$route.query.debug && e.code === 'KeyD' && e.altKey && e.ctrlKey) {
					this.showDebugInfo();
				}
			},
			debugFunc() {
				return {
					promptResponse: this.debug.generateResult,
					voiceSelection: this.requestVoiceDebugInfo,
					...this.debug.customInfo,
				}
			},
			showDebugInfo() {
				this.$notifyMessage('Debug info', {
					JSON: {
						...(this.info.getDebugInfo() || {})
					},
					fullScreen: true
				});
			},
			onRequestVoicesDebugInfo(info) {
				this.requestVoiceDebugInfo.splice(0, 0, {
					timestamp: new Date(),
					...info
				});
			},
			highlightScriptItems() {
				this.isScriptItemsHighlighted++;
				setTimeout(() => this.isScriptItemsHighlighted--, 400);
			},
			setChatBot() {
				this.$chatBotEl = this.$refs.chatBot?.$el;
				if (this.$chatBotEl) {
					document.body.appendChild(this.$chatBotEl);
				}
			},
			removeChatBot() {
				if (this.$chatBotEl) {
					document.body.removeChild(this.$chatBotEl);
					delete this.$chatBotEl;
				}
			},
			selectResponse(response, idx) {
				this.script = response.script;
				this.scriptText = response.scriptText;
				this.lastResponse = response;
				this.currentResponseIdx = idx;
			},
			onNextClick() {
				if (this.numberOfVariantsSpecified) {
					this.activateCustomVoices();
					this.generateBriefImages();
					this.$emit('next');
					this.chatBotVisible = false;
				} else {
					this.numberOfVariantsSpecified = true;
					this.loadAiScript(void 0, void 0, void 0, void 0, { initial: !this.responses.length });
				}
			},
			onBackClick() {
				/*if (this.numberOfVariantsSpecified) {
					this.numberOfVariantsSpecified = false;
				} else*/ {
					this.$emit('back');
					this.chatBotVisible = false;
				}
			},
			async generateBriefImages() {
				if (!this.lastResponse?.logId) return;
				
				this.generatingImages++;
				try {
					const result = await this.$Data.postExternalApi('OpenAiGpt3/GenerateImages', { Id: this.lastResponse?.logId });

					let images = (result?.Data || []).map(f => ({ promptName: f.propmtName, ...f }));

					if (images.length) {
						this.images = (result?.Data || []).map(f => {
							return { 
								promptName: f.propmtName, 
								...f,
								blobPromise: this.$Data.proxyDownload(f.imageUrl, { responseType:'blob' })
							}
						});
					}
				} finally {
					this.generatingImages--;
				}
			},
			sendChatBotMessage(msg, time, opts) {
				this.$eventsBus.$emit('chat-bot-send-message', msg, time, opts);
			},
			async onChatBotUserAttachment(attachment, attachmentText) {
				await this.processAssistant(attachmentText, attachment).catch(e => {
					this.$notifyError(e);
				});
			},
			async onChatBotUserMessage(e) {
				let text = e?.text;	

				if (__DEBUG__)
					console.debug('onChatBotUserMessage', { e, text });

				
				if (text) {
					await this.processAssistant(text).catch(e => {
						this.$notifyError(e);
					});
					this.processingAttachment = null;
				}
			},
			getScriptAssistantInfo(items) {
				return (items || this.script.filter(f => f.type === 'text' && f.actualVoice))
					.map(f => `${f.operation?.split(',')[0]} tag represents ${f.actualVoice.displayName} ${f.voice_gender} voice`)
					.join('\r\n');
			},
			async initAssistant() {
				if (this.assistantInitiated) return;

				this.assistantInitiated = true;
				try {
					let res = await this.$Data.postExternalApi('OpenAiGpt3/run', {
						promptCode: 'BriefChatAssistant',
						assistantThreadId: this.assistantThreadId,
						assistantId: this.assistantId,
						repeatChildrenXTimes: 0,
						promptText: `please help an user to improve the following ad script:\r\n${this.scriptText}\r\n\r\n${this.getScriptAssistantInfo()}`
					});

					this.assistantThreadId = res?.Data?.AssistantThreadId;

					let answers = await this.info.getBriefAnswersOnce();
					if (answers?.Oid) {
						await this.$Data.postExternalApi('OpenAiGpt3/run', {
							promptCode: 'UserIntentionsAssistant',
							assistantThreadId: this.assistantThreadId,
							assistantId: this.assistantId,
							repeatChildrenXTimes: 0,
							briefAnswerId: answers?.Oid
						}).catch((ex) => console.error(ex));
					}
				} finally {
					if (!this.assistantThreadId) {
						this.assistantInitiated = false;
					}
				}
			},
			async checkSwitchAssistantScript() {
				if (this.assistantResponseIdx === this.currentResponseIdx) return;

				await this.$Data.postExternalApi('OpenAiGpt3/run', {
					promptCode: 'BriefChatAssistant',
					assistantThreadId: this.assistantThreadId,
					assistantId: this.assistantId,
					repeatChildrenXTimes: 0,
					promptText: `use this script: r\n${this.scriptText}\r\n\r\n${this.getScriptAssistantInfo()}`
				});

				this.assistantResponseIdx = this.currentResponseIdx;
			},
			async processAssistant(text, attachment, tryNumber) {
				let attachmentBlobName = null;
				this.$eventsBus.$emit('chat-bot-processing', 1);

				try {
					let res = '';
					let content = '';
					
					if (!this.testPayload) {
						if (!this.$initAssistantReq) {
							this.$initAssistantReq = this.initAssistant();
						}
						
						await this.$initAssistantReq;

						await this.checkSwitchAssistantScript();
					
						let payload = {
							promptCode: 'BriefChatAssistant',
							assistantThreadId: this.assistantThreadId,
							assistantId: this.assistantId,
							repeatChildrenXTimes: 0,
							promptText: text
						};

						if (attachment) {
							attachmentBlobName = `tmp_${this.$Data.uuid()}_${attachment.name}`;

							let isAudio = ['wav', 'mp3', 'ogg'].some(f => attachmentBlobName.toLowerCase().endsWith(f));
							let isImage = ['png', 'jpg', 'jpeg', 'ico', 'gif', 'webp'].some(f => attachmentBlobName.toLowerCase().endsWith(f));

							if (isAudio) {
								let tmpAudioBlob = await this.$azure.uploadBlobWithInfoResponse(attachmentBlobName, attachment, 'temporary');

								let text = await this.$Data.speechProcessAudio({ AzureFile: tmpAudioBlob });
								this.$azure.deleteBlob(attachmentBlobName, 'temporary');

								attachmentBlobName = `tmp_${attachment.name}_recognized.txt`;
								attachment = new Blob([text]);
							}

							const azureBlob = await this.$azure.uploadBlobWithInfoResponse(attachmentBlobName, attachment, 'temporary');

							if (__DEBUG__)
								console.debug('Blob uploaded', azureBlob);
							
							const sas = (await this.$azure.getSasForAzureContainer('temporary', 300))?.sasKey;
							let attachmentUrl = azureBlob?.AzureFileUrl + `?${sas}`;

							payload.parameters = {};
							payload.parameters[isImage ? 'IMAGE' : 'FILE'] = attachmentUrl;
							payload.parameters['FILENAME'] = attachment.name;

							if (!text) {
								payload.promptText = isImage ? 'Here is image' : 'Here is file';
							} else {
								payload.parameters['MESSAGE'] = text;
							}
						}

						res = await this.$Data.postExternalApi('OpenAiGpt3/run', payload);
						content = res?.Data?.Content || '';
					}

					/* FOR TEST ONLY */
					if (__DEBUG__ && this.testPayload) {
						content = `Here's translated result:

///***SCRIPT***///
\`\`\`plaintext
{P: Soft}
\`\`\`
{L:en-US}
{D30}
\`\`\`plaintext
{M: Cheerful,Upbeat}
{VCF1,Warm: I just bought the Albert Heijn Chocolate Letter for the kids, and wow—so delicious!}
{VCM2,Warm: Yes, it really is perfect. Made with creamy milk chocolate... just what they deserve after a busy day at school.}
{XS: Chime}
{VCF1,Empathic: And it really helps that it's affordable too. You get quality for a private label price.}
{VCM2,Professional: Exactly, and no more hassle with last-minute presents during Sinterklaas!}
{VAM3,Warm,Professional: Albert Heijn Chocolate Letters—the tastiest surprise for mothers *and* their little ones. Get it today at your Albert Heijn!}
{ME}
\`\`\`

///***MESSAGE***///
I'm happy to have assisted you. Please let me know any more questions.`;
					}

					const scriptTag = '///***SCRIPT***///';
					const msgTag = '///***MESSAGE***///';

					let hasScript = content.indexOf(scriptTag) > -1;
					let hasMessage = content.indexOf(msgTag) > -1;

					let botText = content;
					let script = null;

					let checkMsg = (msg) => {
						if (!msg) return msg;

						if (msg.trim().endsWith(':')) {
							return msg.trim().replace(/:$/, '.');
						}

						return msg;
					}

					if (hasScript) {
						botText = checkMsg(content.split(scriptTag)[0] || '');
						script = content.split(scriptTag)[1];

						if (hasMessage) {
							script = script.split(msgTag)[0];
						}
					}

					if (hasMessage) {
						if (!hasScript) {
							botText = '';
						}
						botText += checkMsg(content.split(msgTag).slice(-1)[0] || '');
					}

					if (!script) {
						script = await this.tryReadScript(content);

						if (__DEBUG__)
							console.debug('Try read script', { script, content });
					}

					if (script) {
						if (await this.processAssistantScriptChange(script, res?.Data?.logId) === false) {
							if ((tryNumber || 0) < 3) {
								if (__DEBUG__)
									console.debug(`Retry assistant ${this.assistantThreadId}`);

								this.$initAssistantReq = null;
								this.assistantInitiated = false;
								this.assistantThreadId = null;
								
								this.processAssistant(text, attachment, (tryNumber || 0) + 1);
								return;
							}
						}
					}

					botText = this.removeRedudantInfo(botText);

					this.sendChatBotMessage(botText?.trim() || this.$tCaption('All done, please check the results.'));

					if (__DEBUG__)
						console.debug('processAssistant', { res, botText, script, content });
				} finally {
					this.$eventsBus.$emit('chat-bot-processing', 0);
				}
			},
			async tryReadScript(text) {
				const VAMScript = await this.prepareScriptVAMFormat(text);
				if (VAMScript?.length) {
					return text;
				}
				return;
			},
			removeRedudantInfo(text) {
				if (!text) return text;

				text = text
					.replace(/\{(.*?)\}/g, '')
					.replace(/`/g, '')
					.replace(/[\n ]{3,}/g, '\n\n');

				return text;
			},
			async processAssistantScriptChange(script, logId) {
				let scriptArray = null;
				let scriptText = script;

				this.$Data.safe(() => scriptArray = JSON.parse(script));
				
				if (scriptArray && Array.isArray(scriptArray)) {
					script = scriptArray;
				} else {
					const VAMScript = await this.prepareScriptVAMFormat(script);
					if (VAMScript) {
						script = VAMScript;
					}
				}

				if (logId && !this.scriptIsParsedWell(script, scriptText)) {
					if (logId) {
						this.$Data.postExternalApi('OpenAiGpt3/MarkLogAsInvalid', { Oid: logId });
					}
					return false;
				}

				let languageTag = script.find(f => f.type === 'language' && f.locale);
				let isNewLanguage = languageTag && languageTag?.locale?.locale !== this.info.language()?.locale;

				if (__DEBUG__)
					console.debug('test 1', { script, isNewLanguage, languageTag });
				

				if (isNewLanguage) {
					this.info.setLanguage(languageTag.locale);
				} else {
					script.filter(f => f.type === 'text').map(f => {
						let existingVoice = (this.script || []).find(e => e.type === 'text' && e.operation === f.operation);
						if (existingVoice?.actualVoice) {
							f.actualVoice = existingVoice.actualVoice;
							this.processItemVoice(f);
						}
					});
				}

				let usedVoices = [];

				if (!isNewLanguage) {
					this.script.filter(f => f.type === 'text' && f.actualVoice).map(f => {
						if (!usedVoices.includes(f.actualVoice?.uniqueId)) {
							usedVoices.push(f.actualVoice?.uniqueId);
						}
					});
				}

				let newVoices = script.filter(f => (isNewLanguage || !f.actualVoice) && f.type === 'text');
				if (newVoices.length) {
					await this.calculateScriptVoices(script.filter(f => !f.actualVoice && f.type === 'text'), usedVoices);

					await this.$Data.postExternalApi('OpenAiGpt3/run', {
						promptCode: 'BriefChatAssistant',
						assistantThreadId: this.assistantThreadId,
						assistantId: this.assistantId,
						repeatChildrenXTimes: 0,
						promptText: `Please note the following changes:\r\n${this.getScriptAssistantInfo(newVoices)}`
					});
				}

				this.script = script;
				this.scriptText = scriptText;

				this.responses[this.currentResponseIdx].script = script;
				this.responses[this.currentResponseIdx].scriptText = scriptText;

				if (__DEBUG__)
					console.debug('processScriptChange', { usedVoices });

				this.highlightScriptItems();

				return script;
			},
			onPopupContentReady: function(e) {
				e.component.content().addEventListener('mouseleave',function() {
					e.component.hide();
				});
			},
			formatJSON(json) {
				return JSON.stringify(json, null, 2);
			},
			toggleVoiceInfo(voice, itemIdx, forcedValue) {
				if (__DEBUG__)
					console.debug('toggleVoiceInfo', { voice, itemIdx, forcedValue });

				if (forcedValue === void 0) {
					forcedValue = this.tooltipVoice !== voice;
				}

				this.tooltipTarget = `#voice-image-${itemIdx}`;
				this.tooltipVoice = forcedValue ? voice : null;

				this.voiceLoaded = !this.tooltipVoice;
			},
			onPronunciationToolSelectionChange(e, text) {
				this.selectedText = text;
				this.selectedScriptItem = this.visibleScriptItems[parseInt(document.activeElement.closest('.script-item')?.getAttribute('idx')) || 0];
			},
			onPronunciationToolChange(text) {
				if (this.selectedScriptItem) {
					this.selectedScriptItem.value = text;
				}
			},
			onPronunciationToolSavePhoneme(phoneme, word, phoneSet) {
				if (!this.selectedScriptItem.phonemes)
					this.$set(this.selectedScriptItem, 'phonemes', []);

				let item = this.selectedScriptItem.phonemes.find(f => f.word === word);
				if (!item) {
					item = {
						word
					}
					this.selectedScriptItem.phonemes.push(item);
				}
				item.phoneme = phoneme;
				item.phoneSet = phoneSet;
			},
			activateCustomVoices() {
				if (this.isArray) {
					const voices = [];
					this.script.filter(f => f.actualVoice?.isCustom && !f.actualVoice?.isActive).map(f => {
						if (!voices.find(v => v.id === f.actualVoice?.id)) {
							voices.push(f.actualVoice);
						}
					});

					if (__DEBUG__)
						console.debug('activateCustomVoices', { voices });
					voices.map(f => activateVoice(f));
					this.voicesActivatedTime = voices.length ? new Date() : null;
					this.voicesMaxActivationDuration = _.max(voices.map(v => v.LastActivationDuration || 60));
				}
			},
			deleteItem(item) {
				this.$confirmMessage({
					title: this.$tCaption('Confirm action', '@actions'),
					text: this.$tCaption('Are you sure you want to delete the row?'),
					handler: () => {
						setImmediate(() => {
							const idx = this.script.findIndex(f => f === item);
							if (idx > -1) {
								this.script.splice(idx, 1);
							}
						});
					}
				});
			},
			
			processLoadingPercent() {
				if (this.loadingPercent < 90) {
					this.loadingPercent += _.random(10, 200) / 100;
					this.$percentLoadingTimeout = setTimeout(this.processLoadingPercent, _.random(200, 750));
				}
			},
			async animateLoadingPercent(startValue) {
				if (this.$percentLoadingTimeout) {
					clearTimeout(this.$percentLoadingTimeout);
					delete this.$percentLoadingTimeout;
				}
				this.loadingPercent = startValue || 5;
				this.processLoadingPercent();
			},
			signalrOnNotification({ eventName, data }) {
				if (data.ParentGeneratorOid) {
					if (eventName === 'AIScriptGenerator.Started') {
						if (data.IsRetry)
							this.status = `Retry <PROCESS>`.replace(/<PROCESS>/g, data.Name);
						else
							this.status = `<PROCESS>..`.replace(/<PROCESS>/g, data.Name);
						setTimeout(() => this.animateLoadingPercent(), 500);
					}
					if (eventName === 'AIScriptGenerator.Ended') {
						if (data.IsSuccessful)
							this.status = `<PROCESS> finished successfully`.replace(/<PROCESS>/g, data.Name);
						else
							this.status = `<PROCESS> failed`.replace(/<PROCESS>/g, data.Name);
					}
				}
			},
			getScriptDuration(text) {
				const allText = text || (this.isArray ? this.script.filter(f => f.type === 'text').map(f => f.value).join('. ') : this.script || '');
				const wordsPerSecond = 180 / 60;
				const words = allText.split(/[ \.]/g).filter(f => f).length;
				let duration = words / wordsPerSecond;
				if (this.isArray) {
					duration += this.script.filter(f => f.type === 'sfx').reduce((res) => res + 2, 0);
				}
				if (__DEBUG__)
					console.debug('Get script duration', { allText, wordsPerSecond, words, duration });
				return duration;
			},
			
			showPronunciationDialog() {
				this.$refs.pronunciationTool.show();
			},
			onPronunciationToolSave(text) {
				this.restoreSelection();
				setImmediate(() => {
					if (this._isDestroyed) return;
					const sourceText = this.selectedText || '';
					const newText = sourceText.replace((sourceText || '').trim(), (text || '').trim());
					if (__DEBUG__)
						console.debug('onPronunciationToolSave', { text, sourceText, newText });
					this.replaceSelectedText(newText);
				});
			},
			onActivate() {
				if (__DEBUG__)
					console.debug('On activate', { script: this.script });

				this.numberOfVariantsSpecified = true;
				this.loadAiScript(void 0, void 0, void 0, void 0, { initial: true });
			},
			onShow() {
				this.chatBotVisible = true;
				this.$nextTick(() => this.setChatBot());
			},
			reloadScript() {
				this.script = null;
				this.loadAiScript(void 0, void 0, void 0, void 0, { initial: true });
			},
			changeScript() {
				this.isEditingScript = false;
				this.creativeOpts = null;
			},
			restoreSelection() {
				if (this.$lastSelection?.target) {
					this.$lastSelection?.target.focus();
					this.$lastSelection?.target.setSelectionRange(this.$lastSelection.start, this.$lastSelection.end);
				}
			},
			calcSelectionText(event) {
				if (event && !event?.target?.activeElement?.classList?.contains('dx-texteditor-input')) {
					this.selectedText = null;
					return;
				}
				if (__DEBUG__)
					console.debug('OnSelectionChange', { event });
				var text = "";
				if (window.getSelection) {
					text = window.getSelection().toString();
				} else if (document.selection && document.selection.type != "Control") {
					text = document.selection.createRange().text;
				}
				this.selectedText = text;
				this.selectedScriptItem = this.visibleScriptItems[parseInt(document.activeElement.closest('.script-item')?.getAttribute('idx')) || 0];
				this.$lastSelection = {
					target: document.activeElement,
					start: document.activeElement?.selectionStart,
					end: document.activeElement?.selectionEnd
				};
				return text;
			},
			replaceSelectedText(replacementText) {
				if (this.$lastSelection?.target) {
					const value = this.$lastSelection.target.value || '';
					this.$lastSelection.target.value = value.substr(0, this.$lastSelection.start) + replacementText + value.substr(this.$lastSelection.end, value.length);
				}
			},
			async loadAiScript(tryNumber, rewriteScriptCommand, attachment, newScript, opts) {
				let { initial, preventNavigation } = (opts || {});

				if (__DEBUG__)
					console.debug('loadAiScript', { initial });

				const questions = this.info.questions();
				if (!questions) return;

				this.script = null;
				this.scriptProcessed = false;
				this.lastResponse = null;
				this.generationRunId = null;

				if (initial && !tryNumber) {
					this.responses = [];
					this.requestVoiceDebugInfo = [];
				}

				let generateResult = null;

				try {
					let responses = [];
					if (this.testPayload) {
						/*responses.push({
							Content: `{L:en-US}{VCM1,Empathic: Hello, ${Math.round(Math.random() * 100)}}`
						});*/
						responses.push({
							Content: `{L:en-US}{VCM1,Empathic: Hello, how are you?}{VCM2,Empathic: Great, did you tase coke?}{VCM1,Empathic: This is cool product.}{VCM2,Empathic: Here I have one.}{VCM1,Empathic: Looks good!}{VCM2,Empathic: Enjoy!}{VCM1,Empathic: Amazing, I'll take one.}`
						});
						/*responses.push({
							Content: `{P: Medium}
{L:Dutch (Netherlands)}
{D30}
{M:Corporate,Uplifting}
{VCM1,Empathic: Hello}
{XS: Knock}
{VCF2,Warm: Ja! Profiteer nu nog van extra hoge kortingen. Binnenkort sluiten alle winkels, dus dit is hét moment om toe te slaan.}
{VCM1,Empathic: Echt waar? Ik zag net die geweldige keukenapparaten én schoonmaakspullen voor een fractie van de prijs!}
{XS: Footsteps}
{VCF2,Warm,Professional: Precies, en met hun wekelijkse Prijspakkers haal je meer uit je budget zonder in te leveren op kwaliteit.}
{VAM3,Professional: Bezoek snel blokker punt en el slash prijspakkers punt hache te em el en ontdek hoe je vandaag nog kunt besparen! Blokker—meer kwaliteit voor minder.}
{ME}
`
						});*/
						/*
						let response = {
							Content: `{M:Corporate,Upbeat}
{VCF1,Confident,Friendly: Hey, heb je weer last van slechte dekking?}
{VCM2,Authoritative,Mature: Met Mobiele Telefoons heb je de beste mobiele dekking, altijd en overal.}
{VCF1,Confident,Friendly: Daarnaast heb je ook de snelste verbindingen voor al je streaming en downloads.}
{XH: Notification ping}
{VCM2,Authoritative,Mature: Klanten zeggen het zelf: 'Ik heb nog nooit zo'n snelle service gehad!'}
{VCF1,Confident,Friendly: Bezoek mobieletelefoons.nl en ontdek hoe onze telefoons jouw leven kunnen verbeteren.}
{VCM2,Authoritative,Mature: Mobiele Telefoons, jouw betrouwbare partner voor ononderbroken verbindingen.}
{ME}`
						};
						responses.push(response);
						responses.push(response);*/

						this.debug.generateResult = _.cloneDeep(responses);

						this.isLoading++;
						if (this.testPayload) {
							this.status = 'Loading';
							this.loadingPercent = 10;
							await (new Promise(res => setTimeout(res, 2000)).finally(() => this.isLoading--));
							this.status = null;
							this.loadingPercent = 0;
						}
					} else {
						this.generationRunId = this.$Data.uuid();
						const briefPayload = {
							aiScriptGeneratorId: questions?.brief?.AiScriptGenerator?.Oid,
							run_id: this.generationRunId,
							brief: {
								briefId: this.info.briefId,
								results: questions.getFormedResults()
							},
							clientIp: this.$auth.userIP()
						}

						if (initial && this.numberOfVariants > 1) {
							briefPayload.parallelRun = this.numberOfVariants - this.responses.length;
						}

						if (!initial) {
							briefPayload.parallelRun = 1;
						}
						
						this.status = null;
						this.loadingPercent = 0;
						setImmediate(() => {
							this.loadingPercent += 5;
							this.animateLoadingPercent();
						});
						
						this.debug.generateResult = null;
						
						this.isLoading++;
						if (rewriteScriptCommand && this.lastResponse) {
							let payload = {
								LogId: this.lastResponse.logId,
								ClientIp: this.$auth.userIP(),
								Request: newScript ? null : rewriteScriptCommand,
								Type: newScript ? 1 : 0
							}

							if (attachment) {
								let dataUri = await this.$Data.blob2Base64(attachment);
								let splitUri = this.$Data.splitDataUri(dataUri);
								let ext = (attachment.name || '').split('.').slice(-1)[0];

								if (['pdf', 'txt', 'docx'].includes(ext)) {
									payload.Base64Document = splitUri.base64;
									payload.DocumentExtension = ext;
								} else {
									payload.SpeechToText = {
										Language: this.info.language()?.locale || 'en-US',
										FileExtension: ext,
										Base64StringData: splitUri.base64
									}
								}
							}

							generateResult = (await this.$Data.postExternalApi(`OpenAiGpt3/Rewrite`, payload).finally(() => this.isLoading--))?.Data;
						} else {
							generateResult = (await this.$Data.postExternalApi(`OpenAiGpt3/Generate`, briefPayload).finally(() => this.isLoading--))?.Data;

							if (generateResult?.AssistantThreadId) {
								this.assistantThreadId = generateResult?.AssistantThreadId;
							}

							if (generateResult?.AssistantId) {
								this.assistantId = generateResult?.AssistantId;
							}
						}

						if (Array.isArray(generateResult)) {
							responses = generateResult.map(f => f?.Data);
						} else {
							responses = [generateResult];
						}

						if (this.generationRunId && responses.some(f => f?.run_id === this.generationRunId)) {
							responses = responses.filter(f => f?.run_id === this.generationRunId)
						}

						this.debug.generateResult = _.cloneDeep(generateResult);
					}

					let parsedResponses = [];
					for(var i = 0; i < responses.length; i++) {
						let response = responses[i];
						let script = '';
						const content = response?.Content;
						if (content) {
							let vals = null;
							this.$Data.safe(() => vals = JSON.parse(content));
							
							if (__DEBUG__)
								console.debug('Load ai script', { vals });

							if (vals?.result?.length) {
								script = vals?.result[0] || 'Script';
							} else {
								script = content || this.$tCaption('Script');
							}
						} else {
							script = this.$tCaption('Script');
						}

						let scriptText = script;

						let scriptArray = null;
						this.$Data.safe(() => scriptArray = JSON.parse(script));
						if (scriptArray && Array.isArray(scriptArray)) {
							script = scriptArray;
						} else {
							const VAMScript = await this.prepareScriptVAMFormat(script);
							if (VAMScript) {
								script = VAMScript;
							}
						}

						if (!this.scriptIsParsedWell(script, scriptText)) {
							if (response?.logId) {
								this.$Data.postExternalApi('OpenAiGpt3/MarkLogAsInvalid', { Oid: response.logId });
							}
							continue;
						}

						this.fixGenderVoiceNumber(script);
						await this.calculateScriptVoices(script, void 0, `tab ${i + 1}`);

						responses[i].script = script;
						responses[i].scriptText = scriptText;

						parsedResponses.push(responses[i]);

						if (initial) {
							this.responses.push(responses[i]);
						}
					}

					if ((initial && this.responses.length < this.numberOfVariants || !parsedResponses.length)  && !(tryNumber > 2)) {
						if (__DEBUG__)
							console.debug('Reload script', { text: this.scriptText, obj: _.cloneDeep(this.script), generateResult });
						return this.loadAiScript((tryNumber || 0) + 1, rewriteScriptCommand, attachment, newScript, opts);
					}

					if (!parsedResponses.length) return;

					if (rewriteScriptCommand || !initial) {
						if (this.responses[this.currentResponseIdx]) {
							_.assign(this.responses[this.currentResponseIdx], parsedResponses[0]);
						}
					} else {
						this.lastResponse = this.responses[0];
						this.currentResponseIdx = 0;
					}

					if (!this.script) {
						this.script = this.responses[0]?.script;
						this.scriptText = this.responses[0]?.scriptText;
					}

					if (rewriteScriptCommand) {
						this.sendChatBotMessage(this.$tCaption('All done, please check the results.')); 
						this.sendChatBotMessage(this.$tCaption('Is there anything else I can help you with?'), 2000); 
					} else {
						if (!this.firstGenerated) {
							this.sendChatBotMessage(this.$tCaption('Can I help you with your script?'), 500, { force: false });
						}
					}

					this.firstGenerated = true;
					this.highlightScriptItems();

					if (this.info.skippedSteps.script && !preventNavigation) {
						this.onNextClick();
					}

					if (__DEBUG__)
						console.debug('Script', { script: this.scriptText, res: this.script, newScript, rewriteScriptCommand });
				} finally {
					this.scriptProcessed = true;
				}
			},
			fixGenderVoiceNumber(script) {
				script = script || this.script;

				if (!Array.isArray(script)) return;
				const genders = {};
				
				script.map(f => {
					f.id = this.$Data.uuid();
					if (f.voice) {
						if (!genders[f.voice_gender]) {
							genders[f.voice_gender] = {};
						}
						if (!genders[f.voice_gender][`num_${f.voice_number}`]) {
							genders[f.voice_gender][`num_${f.voice_number}`] = Object.keys(genders[f.voice_gender]).length + 1;
						}

						f.voice_number = genders[f.voice_gender][`num_${f.voice_number}`];
					}
				});
			},
			async calculateScriptVoices(script, usedVoices, logInfo) {
				script = script || this.script;
				if (!Array.isArray(script)) return;

				const voiceSelector = this.info.voiceSelector();
				const genreAdjectives = script.find(f => f.type === 'genre-adjectives')?.genreAdjectives;
				const voices = voiceSelector.requestVoices([
					...script.filter(f => f.voice_gender === 'male').map(f => ({
						key: `${f.voice_gender}_${f.voice_number}`,
						filter: (v) => v.voice.Gender === 'Male' && (!usedVoices?.length || !usedVoices.includes(v => v.uniqueId)),
						adjectives: f.voice?.split(',').map(f => f?.trim()).filter(f => f),
						detailedAdjectives: f.voiceAdjectives,
						genreAdjectives
					})),
					...script.filter(f => f.voice_gender === 'female').map(f => ({
						key: `${f.voice_gender}_${f.voice_number}`,
						filter: (v) => v.voice.Gender === 'Female' && (!usedVoices?.length || !usedVoices.includes(v => v.uniqueId)),
						adjectives: f.voice?.split(',').map(f => f?.trim()).filter(f => f),
						detailedAdjectives: f.voiceAdjectives,
						genreAdjectives
					}))
				], logInfo);
				
				const getVoice = (gender, number) => {
					let voice = voices[`${gender}_${number}`];
					if (!voice) {
						let genderVoices = Object.keys(voices).filter(f => f.startsWith(gender));
						voice = voices[genderVoices[_.random(genderVoices.length - 1)]];
						if (__DEBUG__)
							console.debug('getVoice', { genderVoices, voice });
					}
					if (!voice) {
						voice = voices[_.random(voices.length - 1)];
					}
					return voice;
				}

				script.filter(f => f.type.toLowerCase() === 'text' && !f.actualVoice).map(f => {
					f.actualVoice = (this.testPayload && window.testVoice) || getVoice(f.voice_gender, f.voice_number) || voiceSelector?.getRandomVoice();
					f.trackNames = [`${f.voice_gender} ${f.voice_number}`];
					f.trackTemplates = [`${f.voice_gender}`, 'voice'];
					this.processItemVoice(f);
				});

				if (__DEBUG__)
					console.debug('calculateScriptVoices', { voices, script });
			},
			processItemVoice(item) {
				if (!item) return;

				item.buildInSingleCall = item.actualVoice?.provider?.UseSeedForSelfService;
			},
			scriptIsParsedWell(script, scriptText) {
				script = script || this.script;
				scriptText = scriptText || this.scriptText;

				let scriptPart = script.map(f => f.source).join('').replace(/[ \r\n\t\u00A0]/g, '');
				let textPart = scriptText?.replace(/[ \r\n\t\u00A0]/g, '');
				let len = Math.abs(scriptPart?.length/textPart?.length);
				let result = Array.isArray(script) && len >= 0.9;

				if (__DEBUG__)
					console.debug(`scriptIsParsedWell ${result}`, { len, scriptPart, textPart, script, scriptText });

				return result;
			},
			async prepareScriptVAMFormat(script, preventLog) {
				const templates = `${script}`.match(/\[[T][1-9]?[0-9]?[0-9]?:(.*?)\]/g) || [];

				if (__DEBUG__ && !preventLog)
					console.debug('prepareScriptVAMFormat templates', { templates, script });

				templates.map(t => {
					const str = t.substr(1, t.length-2);
					const templateId = str.split(':')[0];
					const templateBody = str.split(':').slice(1,99999).join(':');
					const templateVariants = [` ${templateId} `, `{${templateId} `, ` ${templateId}}`, `{${templateId}}`, `{${templateId}:`, `:${templateId}}`, `:${templateId} `, `:${templateId}:`];
					const matches = `${script}`.match(new RegExp(templateVariants.join('|'), 'g')) || [];

					if (templateId) {
						matches.map(m => {
							script = script.replace(m, m.replace(templateId, templateBody));
						});

						if (__DEBUG__ && !preventLog)
							console.debug('prepareScriptVAMFormat template', { t, templateId, templateBody, matches, script });
					}
				});

				const getRootElems = (text) => {
					if (!text)  return [];
					const result = [];
					
					let isBracketOpenedCount = 0;
					let currentOperation = '';
					for(var i = 0; i < text.length; i++) {
						if (text[i] === '{') {
							if (isBracketOpenedCount === 0) {
								currentOperation = '';
							}
							isBracketOpenedCount++;
						}

						if (isBracketOpenedCount > 0) {
							currentOperation += text[i];
						}

						if (text[i] === '}') {
							if (isBracketOpenedCount === 1) {
								result.push(currentOperation);
							}
							isBracketOpenedCount--;
						}
					}

					return result;
				};

				const elemRegexes = [
					{ full: '{[V][A-z][FM][0-9][,]?(.*?):(.*?)}', start: '{[V][A-z][FM][0-9][,]?(.*?):' },
					{ full: '{[M][E]?[1-9]?[0-9]?[0-9]?(.*?):?(.*?)}', start: '{[M][E]?[1-9]?[0-9]?[0-9]?' },
					{ full: '{[X][A][E]?(.*?):?(.*?)}', start: '{[X][A][E]?' },
					{ full: '{[X][HS]?(.*?):?(.*?)}', start: '{[X][HS]?' },
					{ full: '{[P](.*?):?(.*?)}', start: '{[P]' },
					{ full: '{[D][0-9]?[0-9]?[0-9]?[F]?(.*?):?(.*?)}', start: '{[D][0-9]?[0-9]?[0-9]?[F]?' },
					{ full: '{GNR[,]?(.*?):?(.*?)}', start: '{GNR[,]?' },
					{ full: '{L:?(.*?)}', start: '{L:' },
				];
				
				const elems = getRootElems(`${script}`);
				if (!elems?.length) return;

				const result = [];
				const adjectives = await getVoicesAdjectives({ full: true });
				let textElemCounter = 0;

				const processElems = (elems) => {
					if (!elems?.length) return;
					elems.map(el => {
						const val = el.match(new RegExp(elemRegexes.map(f => f.start).join('|'), 'g'));
						if (val && val[0]) {
							const operation = val[0].replace(/[\{\}\:]/g,'');
							const operationData = `${el.split(':').slice(1,9999).join(':') || ''}`.trim();
							const operationDataElems = getRootElems(operationData);
							processElems(operationDataElems);

							let text = operationData || '';
							operationDataElems.map(f => {
								text = text.replace(new RegExp(f, 'g'), '');
							})

							text = text.replace(/}/g,'').trim();

							if (__DEBUG__ && !preventLog)
								console.debug('OP', { operation, text, el, val });

							if (operation[0] === 'M') {
								const length = parseInt(operation.replace(/M/g,'')) || 0;
								result.push({ 
									type: operation[1] === 'E' ? 'MUSICEND' : 'MUSIC',
									value: text,
									length,
									description: operation[1] === 'E' ? this.$tCaption('Music ends') : this.$tCaption('Music starts'),
									afterText: textElemCounter,
									source: el
								});
							}

							if (operation[0] === 'D') {
								const length = parseInt(operation.replace(/[DF\}]/g,'')) || 0;
								const isFixed = operation.endsWith('F');
								result.push({ 
									type: 'DURATION',
									operation,
									length,
									isFixed,
									description: isFixed ? this.$tCaption('Fixed duration') : this.$tCaption('Duration'),
									hide: !isFixed,
									source: el
								});
							}

							if (operation[0] === 'V') {
								const voiceAdjectives = operation.split(',').slice(1,999).filter(f => f !== ':');
								result.push({ 
									type: 'text',
									voice: adjectives.find(f => `${f.Key}`.toLowerCase() === `${operation[1]}`.toLowerCase())?.Name,
									shortAdjective: operation[1],
									voice_gender: operation[2] === 'M' ? 'male' : 'female',
									voice_number: parseInt(operation[3]) || 1,
									value: text,
									voiceAdjectives: voiceAdjectives.map(a => adjectives.find(f => 
										((a || '').length === 1 && `${f.Key}`.toLowerCase() === `${operation[1]}`.toLowerCase()) ||
										(`${f.Name}`.toLowerCase() === `${a}`.toLowerCase()) ||
										((a || '').length > 1 && `${f.Name}`.startsWith(`${a}`))
									)?.Name).filter(f => f),
									source: el,
									operation
								});
								textElemCounter++;
							}

							if (operation[0] === 'X') {
								let sfx = {
									type: operation[1] === 'A' ? (operation[2] === 'E' ? 'ambience-end' : 'ambience') : 'sfx',
									description: operation[1] === 'A' ? (operation[2] === 'E' ? this.$tCaption('Ambience SFX ends') : this.$tCaption('Ambience SFX')) : 'SFX',
									afterText: textElemCounter,
									value: text,
									trackNames: operation[1] === 'A' ? ['SFX ambience', 'sfx'] : [operation[1] === 'S' ? 'SFX soft' : 'SFX hard', 'SFX effects', 'sfx'],
									source: el
								}
								result.push(sfx);
								if (sfx.type === 'sfx') {
									textElemCounter++;
								}
							}

							if (operation[0] === 'P') {
								result.push({
									type: 'processing-template',
									description: this.$tCaption('"<TEMPLATE>" Processing').replace(/<TEMPLATE>/g, text),
									keyword: text,
									source: el
								});
							}

							if (operation.slice(0,3) === 'GNR') {
								const genreAdjectives = (operationData || '').replace(/\}/g,'').split(',').filter(f => f !== ':');
								result.push({
									type: 'genre-adjectives',
									description: `${this.$tCaption('Genre')}: ${genreAdjectives.join(', ')}`,
									genreAdjectives,
									source: el
								});
							}

							if (operation.slice(0,1) === 'L') {
								result.push({
									type: 'language',
									hide: true,
									source: el,
									locale: this.info.filteredLanguages.find(f => f.locale?.toLowerCase() === text?.toLowerCase() || f.name?.toLowerCase() === text?.toLowerCase() || f.text?.toLowerCase() === text?.toLowerCase())
								});
							}

							if (__DEBUG__ && !preventLog)
								console.debug('prepareScriptVAMFormat oper', { operation, val, operationData, operationDataElems });
						}
					});
				};
				processElems(elems);

				if (__DEBUG__ && !preventLog)
					console.debug('prepareScriptVAMFormat', { result, adjectives, elems, script });

				return result;
			}
		}
	};
</script>
<style lang='scss'>
	@import "main/themes/generated/variables.base.scss";
	.easy-creative--script-builder {
		min-height: 300px;

		@media all and (min-width: 720px) {
			width: 720px;
		}

		.play-button {
			padding: 10px 20px;
			border-radius: 4px;
			background: #fff;
			box-shadow: 2px 2px 13px 2px rgba(0,0,0,0.1);
			transition: 0.3s;
			cursor: pointer;

			&:hover {
				box-shadow: 2px 2px 13px 2px $base-accent-transparent;
				color: $base-accent;
			}
		}

		.script-items {
			border-radius: 4px;
			overflow: hidden;
			outline: 2px solid var(--branding-bg);
			transition: 0.3s;

			&.active {
				outline: 2px solid var(--branding-highlight-color);
				animation: heartbeat 0.3s ease-in-out 1 both;
			}
		}

		.script-item {
			padding: 10px;
			background: var(--bg);
			border-radius: 2px;

			> td {
				padding: 10px 20px;
				vertical-align: top;
			}

			&.not-text {
				font-size: 10px !important;
				
				> td {
					vertical-align: middle;
					padding: 4px 18px;
				}

				> .role-td, > .text-td {
					color: rgba(0,0,0,0.3);
				}
			}
			
			.role {
				margin-top: 3px;
			}

			.text-td {
				padding-left: 0px !important;
				width: 100%;
				position: relative;

				.actions-holder {
					position: absolute;
					top: -2px;
					right: 10px;
					font-size: 10px;

					.action-button {
						cursor: pointer;
						border-radius: 40px;
						min-width: 24px;
						min-height: 24px;
						opacity: 1;
						background: var(--branding-button-bg);
						color: var(--branding-button-color);
						transition: 0.3s;

						&:hover {
							background: var(--branding-active-bg);
							color: var(--branding-active-color);
						}
					}
				}
			}

			.role-td {
				padding-right: 12px;
				text-align: end;
				//border-left: 5px solid #ddd;
			}
		}

		.voice-script-elem {
			position: relative;

			> .adjectives {
				position: absolute;
				bottom: -9px;
				right: 4px;
				font-size: 9px;
				text-transform: lowercase;
				font-weight: 100;
			}
		}

		.script-text-area {
			background: var(--branding-bg) !important;
			color: var(--branding-color) !important;
		}

		.editable-item-title {
			cursor: pointer;

			&:hover {
				color: var(--branding-highlight-color);
			}
		}

		.phonemes {
			.phoneme {
				padding: 1px 4px;
				background: var(--branding-bg);
				color: var(--branding-color);
				border-radius: 4px;
				font-size: 10px;

				> .word {
					opacity: 0.5;
				}
			}
		}

		.voice-image-holder {
			position: relative;
			width: 40px;

			.voice-image {
				position: absolute;
				border: 3px solid var(--branding-bg);
				width: 40px;
				height: 40px;
				background: var(--image-url);
				background-size: cover;
				background-position: center;
				border-radius: 80px;
				top: 50%;
				left: 0px;
				transform: translateY(-50%);
			}
		}

		pre {
			white-space: pre-wrap;
		}

		@keyframes heartbeat {
			from {
				transform: scale(1);
			}			
			30% {
				transform: scale(1.05);
			}
			100% {
				transform: scale(1);
			}
		}
	}

	.easy-creative--script-builder--voice-details-tooltip {
		max-width: 50vw;
		min-width: 600px;

		&:not(.loaded) {
			min-height: 400px;
		}

		* {
			white-space: normal !important;
		}
	}
</style>
